import {
  GetListsQueryVariables,
  Shop_Lists_Entity,
  Shop_Lists_PrivacyValue,
} from '@app/data/shop-bff';
import logger from '@redbubble/next/logger';
import { MutableRefObject, useCallback, useState } from 'react';
import { ListsInclusions, UserLists } from '../types/shop-bff/types';
import { publicShopBffSdk } from './common';

export const isItemInList = (
  listId?: string,
  itemListsInclusions?: ListsInclusions | null,
  inventoryItemId?: string
): boolean => {
  if (!inventoryItemId || !listId || !itemListsInclusions) {
    return false;
  }

  const lists = itemListsInclusions.find((listInclusion) => {
    return (
      listInclusion.id ===
      `${Shop_Lists_Entity.InventoryItem}_${inventoryItemId}`
    );
  })?.lists;

  if (!lists) return false;

  return lists.some((l) => l.listId === listId);
};

export const itemIsIncludedInAnyList = (
  inventoryItemId: string,
  listsInclusions: ListsInclusions
): boolean => {
  const list = listsInclusions.find((listInclusion) => {
    return (
      listInclusion.id ===
      `${Shop_Lists_Entity.InventoryItem}_${inventoryItemId}`
    );
  })?.lists;

  if (!list) return false;
  return list.length > 0;
};

export const addItemToList = async (
  inventoryItemId: string,
  listId: string,
  locale: string
) => {
  return publicShopBffSdk
    .AddItemOrWorkToList({
      input: {
        listId,
        locale,
        item: {
          entityId: inventoryItemId,
          entityType: Shop_Lists_Entity.InventoryItem,
        },
      },
    })
    .then((res) => {
      if (res.shop_addItemToList?.inclusions?.lists) {
        return res.shop_addItemToList.inclusions.lists;
      }

      return [];
    });
};

export const removeItemFromList = async (
  inventoryItemId: string,
  listId: string,
  locale: string
) => {
  return publicShopBffSdk
    .RemoveItemOrWorkFromList({
      input: {
        listId,
        locale,
        item: {
          entityId: inventoryItemId,
          entityType: Shop_Lists_Entity.InventoryItem,
        },
      },
    })
    .then((res) => {
      if (res.shop_removeItemFromList?.inclusions?.lists) {
        return res.shop_removeItemFromList.inclusions.lists;
      }

      return [];
    });
};

export const addItemToDefaultList = async (
  inventoryItemId: string,
  locale: string
) => {
  return publicShopBffSdk
    .AddItemOrWorkToDefaultList({
      input: {
        locale,
        item: {
          entityId: inventoryItemId,
          entityType: Shop_Lists_Entity.InventoryItem,
        },
      },
    })
    .then((res) => {
      if (
        res.shop_addItemToDefaultList?.inclusions?.lists &&
        res.shop_addItemToDefaultList.defaultList?.name
      ) {
        return {
          lists: res.shop_addItemToDefaultList.inclusions.lists,
          defaultListName: res.shop_addItemToDefaultList.defaultList.name,
        };
      }
      return null;
    });
};

export const createNewList = async (name: string) => {
  return publicShopBffSdk
    .CreateList({
      input: {
        name,
        privacy: Shop_Lists_PrivacyValue.Public,
      },
    })
    .then((res) => {
      if (res.shop_createList?.list) {
        return res.shop_createList.list;
      }
      return null;
    });
};

export const useUserLists = () => {
  const [{ userLists, loading }, setUserLists] = useState<{
    userLists: UserLists | undefined | null;
    loading: boolean;
  }>({ userLists: null, loading: true });

  const refresh = useCallback((variables: GetListsQueryVariables) => {
    setUserLists({ userLists: null, loading: true });

    publicShopBffSdk
      .GetLists(variables)
      .then((res) => {
        setUserLists({
          userLists: res.shop_lists_lists,
          loading: false,
        });
      })
      .catch((err) => {
        const message = 'An error occurred while fetching user lists';
        logger.error({ err, message, context: 'useUserLists' }, message);
        setUserLists({
          userLists: null,
          loading: false,
        });
      });
  }, []);

  return {
    userLists,
    loading,
    refresh,
    setUserLists,
  };
};

export const updateListItem = (
  newListInclusions: {
    id: string;
    listId: string;
  }[],
  checkboxQueue: MutableRefObject<Map<string, boolean>>
) => {
  let tempListInclusions = [...newListInclusions];

  checkboxQueue.current.forEach((isChecked, listId) => {
    if (
      isChecked &&
      !tempListInclusions.some((list) => list.listId === listId)
    ) {
      tempListInclusions = [...tempListInclusions, { id: 'temp_id', listId }];
    } else {
      tempListInclusions = tempListInclusions.filter(
        (list) => list.listId !== listId
      );
    }
  });

  return tempListInclusions;
};
